import { instance } from "api/apiBase";
import { useMutation } from "@tanstack/react-query";
import { useRef, useState, useCallback, useEffect } from "react";
import { PropertyGroupSplitBy } from "../types";
import {
  GroupTree,
  PropertyGroup,
  type SplitData
} from "../helpers/group_tree";
import { generateInitialPropertyGroups } from "../utils";
import { propertyGroupTitleMap } from "../constants";
import { plural } from "pluralize";

export const useSegment = (
  splitByOptions: PropertyGroupSplitBy[],
  existingPropertyGroups?: PropertyGroup[]
) => {
  const groupTree = useRef<GroupTree>();
  const abortRef = useRef<AbortController | null>(null);
  const initializedFromStorage = useRef<boolean>(false);

  if (!groupTree.current) groupTree.current = new GroupTree();

  const [segments, setSegments] = useState([]);

  const abortActiveRequests = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
  }, []);

  const computePropertyCounts = useCallback(async () => {
    if (!groupTree.current) return null;

    abortActiveRequests();
    abortRef.current = new AbortController();

    const groups = groupTree.current.transformToPropertyGroups();
    const groupTreeRef = groupTree.current;

    try {
      const response = await instance.post(
        "/portal/v1/property_groups/compute_property_counts",
        {
          group_data: groups.map((group) => ({
            identity: group.identity,
            split_data_chain: group.splitDataChain.map((data) => ({
              split_type: data.splitType,
              split_values: data.splitValues,
              is_other: data.isOther
            }))
          }))
        },
        { signal: abortRef.current.signal }
      );

      const newGroups = groups.map((group) => ({
        ...group,
        totalProperties:
          response.data.nodes.find((item) => item.identity === group.identity)
            ?.total_properties || 0
      }));

      groupTreeRef.buildTree(newGroups);
      const nodes = groupTreeRef.transformToNodes();
      setSegments(nodes);

      if (initializedFromStorage.current)
        initializedFromStorage.current = false;

      return response.data;
    } catch (err: any) {
      if (err.name === "CanceledError" || err.name === "AbortError") return;
      throw err;
    }
  }, [abortActiveRequests]);

  const { mutate, isPending: isLoading } = useMutation({
    mutationFn: computePropertyCounts
  });

  const getGroups = useCallback(() => {
    if (!groupTree.current?.getTree()) return [];
    return groupTree.current.transformToPropertyGroups();
  }, []);

  const processStoredPropertyGroups = useCallback(
    (storedGroups: any[]) => {
      return storedGroups.map((group, index) => {
        const splitDataChain: SplitData[] = [
          {
            splitType: "",
            splitValues: [],
            isOther: true
          }
        ];

        const startIdx = group.split_data_chain[0]?.split_type === "" ? 1 : 0;
        for (let i = startIdx; i < group.split_data_chain.length; i++) {
          const data = group.split_data_chain[i];
          splitDataChain.push({
            splitType: data.split_type || "",
            splitValues: data.split_values || [],
            isOther: !!data.is_other
          });
        }

        while (splitDataChain.length < splitByOptions.length + 1) {
          const splitTypeIndex = splitDataChain.length - 1;
          const splitType =
            splitTypeIndex < splitByOptions.length
              ? splitByOptions[splitTypeIndex]
              : "";

          splitDataChain.push({
            splitType,
            splitValues: [],
            isOther: true
          });
        }

        const titleChain = ["root"];
        const titleStartIdx = group.title_chain?.[0] === "root" ? 1 : 0;

        if (group.title_chain && group.title_chain.length > 0)
          for (let i = titleStartIdx; i < group.title_chain.length; i++)
            titleChain.push(
              group.title_chain[i] ||
                `Other ${plural(propertyGroupTitleMap[splitByOptions[i]] || "")}`
            );

        while (titleChain.length < splitDataChain.length)
          titleChain.push(
            `Other ${plural(propertyGroupTitleMap[splitByOptions[titleChain.length - 2]] || "")}`
          );

        if (titleChain.length > splitDataChain.length)
          titleChain.length = splitDataChain.length;

        return {
          splitDataChain,
          titleChain,
          identity: `pg_${group.id}`,
          index,
          verificationTemplateIdentifier:
            group.verification_template?.id || null,
          totalProperties: group.total_properties || 0
        };
      });
    },
    [splitByOptions]
  );

  const initializeGroupTree = useCallback(() => {
    groupTree.current!.setSplitBy(splitByOptions);

    if (existingPropertyGroups && existingPropertyGroups.length > 0) {
      const transformedGroups = processStoredPropertyGroups(
        existingPropertyGroups
      );

      if (transformedGroups.length > 0) {
        groupTree.current!.buildTree(transformedGroups);
        initializedFromStorage.current = true;
      } else
        groupTree.current!.buildTree([
          generateInitialPropertyGroups(splitByOptions)
        ]);
    } else
      groupTree.current!.buildTree([
        generateInitialPropertyGroups(splitByOptions)
      ]);

    mutate();
  }, [
    splitByOptions,
    mutate,
    processStoredPropertyGroups,
    existingPropertyGroups
  ]);

  useEffect(() => {
    initializeGroupTree();

    return () => {
      abortActiveRequests();
    };
  }, [splitByOptions, abortActiveRequests, existingPropertyGroups]);

  const updateGroupTree = useCallback(
    (
      nodeId: string,
      splitDatas: Array<{
        title: string;
        splitValues: string[];
        isOther: boolean;
      }>,
      onSuccess?: () => void
    ) => {
      groupTree.current!.updateChildren(nodeId, splitDatas);

      mutate(undefined, {
        onSuccess: () => {
          onSuccess?.();
        }
      });
    },
    [mutate]
  );

  return {
    isLoading,
    segments,
    updateGroupTree,
    refetch: () => mutate(),
    getGroups
  };
};
